import { useCallback, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getEGiftCardsByMerchantBrandId } from "../api/eGiftCard.api";
import type { ExistingEGiftCardCampaignResponse } from "../types/eGiftCard.types";
import {
  getEGiftCardCampaignRequestKey,
  type MerchantBrandItemCampaignSelection,
} from "../utils/eGiftCardCampaignRequest";

interface UseEGiftCardByMerchantBrandIdReturn {
  eGiftCards: ExistingEGiftCardCampaignResponse[];
  isLoading: boolean;
  error: unknown;
  validatedRequestKey: string | null;
  fetchEGiftCardsByMerchantBrandId: (
    merchantBrandId: string,
    merchantBrandItems: MerchantBrandItemCampaignSelection[],
    couponId?: string,
  ) => Promise<ExistingEGiftCardCampaignResponse[]>;
  clearEGiftCards: () => void;
}

export const useEGiftCardByMerchantBrandId = (): UseEGiftCardByMerchantBrandIdReturn => {
  const [eGiftCards, setEGiftCards] = useState<ExistingEGiftCardCampaignResponse[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [validatedRequestKey, setValidatedRequestKey] = useState<string | null>(null);
  const { run: runLatestRequest, cancel, loading } = useLatestRequest();

  const fetchEGiftCardsByMerchantBrandId = useCallback(
    async (
      merchantBrandId: string,
      merchantBrandItems: MerchantBrandItemCampaignSelection[],
      couponId?: string,
    ): Promise<ExistingEGiftCardCampaignResponse[]> => {
      const requestKey = getEGiftCardCampaignRequestKey(
        merchantBrandId,
        merchantBrandItems,
        couponId,
      );
      setError(null);
      setValidatedRequestKey(null);

      try {
        const responses = await runLatestRequest((signal) =>
          Promise.all(
            merchantBrandItems.map(({ merchantBrandItemId, pointRequired }) =>
              getEGiftCardsByMerchantBrandId(
                merchantBrandId,
                merchantBrandItemId,
                pointRequired,
                couponId,
                signal,
              ),
            ),
          ),
        );
        if (!responses) return [];

        const eGiftCards = responses.flat();
        setEGiftCards(eGiftCards);
        setValidatedRequestKey(requestKey);
        return eGiftCards;
      } catch (requestError: unknown) {
        setError(requestError);
        setEGiftCards([]);
        setValidatedRequestKey(null);
        return [];
      }
    },
    [runLatestRequest],
  );

  const clearEGiftCards = useCallback(() => {
    cancel();
    setError(null);
    setEGiftCards([]);
    setValidatedRequestKey(null);
  }, [cancel]);

  return {
    eGiftCards,
    isLoading: loading,
    error,
    validatedRequestKey,
    fetchEGiftCardsByMerchantBrandId,
    clearEGiftCards,
  };
};
